import React, { useState, useEffect, useCallback } from 'react';
import { invoicesApi, configApi, studentsApi, feeStructuresApi } from '../services/api';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import CurrencySelector from '../components/CurrencySelector';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MultiSelect } from '../components/MultiSelect';
import { ChevronLeft, ChevronRight, Loader2, Plus, Search, Download, FileText, Trash2, Pencil, Lock } from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import { useBulkSelect, BulkDeleteButton, BulkSelectionBar } from '../components/BulkDelete';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router-dom';

const formatCurrency = (amount, currency = 'USD') => {
  if (!amount && amount !== 0) return '-';
  return `${currency} ${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const InvoicesPage = () => {
  const navigate = useNavigate();
  const { isAdmin, userBranches } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const sel = useBulkSelect();
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${sel.size} selected invoice(s)? Any payments applied to them are returned to the student's credit. This cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      const res = await invoicesApi.bulkDelete(Array.from(sel.selected));
      toast.success(res.data.message || 'Deleted');
      sel.clear();
      fetchInvoices();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    } finally {
      setBulkDeleting(false);
    }
  };
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState([]);
  // ?fee_head=a,b (Dashboard Revenue-wise card) pre-selects the Fee Head filter.
  const [searchParams] = useSearchParams();
  const [feeHeadFilter, setFeeHeadFilter] = useState(() => {
    const h = searchParams.get('fee_head');
    return h ? h.split(',') : [];
  });
  const [branchFilter, setBranchFilter] = useState([]);
  const [classFilter, setClassFilter] = useState([]);
  const [termFilter, setTermFilter] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Invoice Dialog
  const [showDialog, setShowDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [studentSearch, setStudentSearch] = useState('');
  const [studentResults, setStudentResults] = useState([]);
  const [searchingStudent, setSearchingStudent] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [availableFees, setAvailableFees] = useState([]);
  const [loadingFees, setLoadingFees] = useState(false);
  
  // Multi-line invoice state
  const [invoiceData, setInvoiceData] = useState({
    academic_year: '2025-2026',
    term: '',
    due_date: '',
    line_items: []
  });
  
  // For adding new line item
  const [newLineItem, setNewLineItem] = useState({
    fee_head: '',
    sub_category: '',
    amount: '',
    description: ''
  });
  
  // Duplicate check state
  const [duplicateCheckResult, setDuplicateCheckResult] = useState(null);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);

  // Edit state
  const [editInvoice, setEditInvoice] = useState(null);
  const [editData, setEditData] = useState({});
  const [editSubmitting, setEditSubmitting] = useState(false);

  const handleEditSave = async () => {
    if (!editInvoice) return;
    setEditSubmitting(true);
    try {
      await api.put(`/invoices/${editInvoice.id}`, editData);
      toast.success('Invoice updated');
      setEditInvoice(null);
      fetchInvoices();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update invoice');
    } finally {
      setEditSubmitting(false);
    }
  };

  const openEdit = (inv) => {
    setEditInvoice(inv);
    setEditData({
      amount: inv.amount,
      currency: inv.currency || 'USD',
      fee_head: inv.fee_head || '',
      term: inv.term || '',
      due_date: inv.due_date || '',
      description: inv.description || ''
    });
  };

  const fetchConfig = useCallback(async () => {
    try {
      const response = await configApi.get();
      setConfig(response.data);
    } catch (err) {
      console.error('Failed to fetch config:', err);
    }
  }, []);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 25 };
      if (statusFilter.length) params.status = statusFilter.join(',');
      if (feeHeadFilter.length) params.fee_head = feeHeadFilter.join(',');
      if (branchFilter.length) params.branch = branchFilter.join(',');
      if (classFilter.length) params.current_class = classFilter.join(',');
      if (termFilter.length) params.term = termFilter.join(',');
      if (searchQuery) params.search = searchQuery;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const response = await invoicesApi.getAll(params);
      setInvoices(response.data.invoices);
      setTotalPages(response.data.pages);
      setTotal(response.data.total);
    } catch (err) {
      toast.error('Failed to load invoices');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, feeHeadFilter, branchFilter, classFilter, termFilter, searchQuery, startDate, endDate]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  // Fetch available fees when student and term are selected
  const fetchAvailableFees = async (student, term, academicYear) => {
    if (!student || !term) return;
    setLoadingFees(true);
    try {
      const response = await feeStructuresApi.getAll({
        branch: student.branch,
        class_name: student.current_class,
        term: term,
        academic_year: academicYear,
        limit: 100
      });
      setAvailableFees(response.data.fee_structures || []);
    } catch (err) {
      console.error('Failed to fetch fees:', err);
      setAvailableFees([]);
    } finally {
      setLoadingFees(false);
    }
  };

  // Student search with debounce
  const searchStudents = async (query) => {
    if (query.length < 2) {
      setStudentResults([]);
      return;
    }
    setSearchingStudent(true);
    try {
      const response = await studentsApi.getAll({ search: query, limit: 10 });
      setStudentResults(response.data.students || []);
    } catch (err) {
      console.error(err);
    } finally {
      setSearchingStudent(false);
    }
  };

  const handleStudentSelect = (student) => {
    setSelectedStudent(student);
    setStudentResults([]);
    setStudentSearch('');
    // Fetch fees if term already selected
    if (invoiceData.term) {
      fetchAvailableFees(student, invoiceData.term, invoiceData.academic_year);
    }
  };

  const handleTermChange = (term) => {
    setInvoiceData({ ...invoiceData, term });
    if (selectedStudent) {
      fetchAvailableFees(selectedStudent, term, invoiceData.academic_year);
    }
  };

  // Fee heads that are always available with open (manual) amount entry
  const openAmountFeeHeads = ['transport'];

  // Build merged fee head options: every supported fee head is always selectable.
  // When a matching fee_structure exists for this branch/class/term, its amount
  // is pre-filled; otherwise the user enters the amount manually.
  const getFeeHeadOptions = () => {
    const options = [];
    const addedHeads = new Set();

    if (availableFees.length > 0) {
      availableFees.forEach((fee) => {
        if (!addedHeads.has(fee.fee_head) && !openAmountFeeHeads.includes(fee.fee_head)) {
          const labelName = config?.fee_head_display?.[fee.fee_head] || fee.fee_head.replace(/_/g, ' ');
          options.push({
            value: fee.fee_head,
            label: `${labelName} (${formatCurrency(fee.amount, fee.currency)})`,
            hasAmount: true,
          });
          addedHeads.add(fee.fee_head);
        }
      });
    }

    // Always offer every supported fee head, even when no fee_structure entry exists.
    feeHeads.filter(h => !openAmountFeeHeads.includes(h) && !addedHeads.has(h)).forEach((head) => {
      const labelName = config?.fee_head_display?.[head] || head.replace(/_/g, ' ');
      options.push({ value: head, label: `${labelName} (enter amount)`, hasAmount: false });
      addedHeads.add(head);
    });

    // Open-amount heads (transport) at the end
    openAmountFeeHeads.forEach((head) => {
      if (!addedHeads.has(head)) {
        const labelName = config?.fee_head_display?.[head] || head.replace(/_/g, ' ');
        options.push({ value: head, label: `${labelName} (enter amount)`, hasAmount: false });
      }
    });

    return options;
  };

  // All sub_categories available for the currently-picked fee_head.
  const getSubCategoriesForHead = (feeHead) => {
    if (!feeHead) return [];
    const seen = new Set();
    const subs = [];
    availableFees
      .filter(f => f.fee_head === feeHead)
      .forEach(f => {
        const sub = f.sub_category || "";
        if (!seen.has(sub)) {
          seen.add(sub);
          subs.push({ sub_category: sub, amount: f.amount, currency: f.currency });
        }
      });
    return subs;
  };

  const handleFeeHeadSelect = (feeHead) => {
    const subs = getSubCategoriesForHead(feeHead);
    // If exactly one fee_structure matches, pre-fill its amount and sub_category.
    if (subs.length === 1) {
      setNewLineItem({
        fee_head: feeHead,
        sub_category: subs[0].sub_category,
        amount: subs[0].amount.toString(),
        description: '',
      });
    } else {
      // Multiple sub_categories or none — wait for user to pick.
      setNewLineItem({ fee_head: feeHead, sub_category: '', amount: '', description: '' });
    }
  };

  const handleSubCategorySelect = (sub) => {
    const match = availableFees.find(
      f => f.fee_head === newLineItem.fee_head && (f.sub_category || '') === sub
    );
    setNewLineItem({
      ...newLineItem,
      sub_category: sub,
      amount: match ? match.amount.toString() : '',
    });
  };

  const addLineItem = () => {
    if (!newLineItem.fee_head) {
      toast.error('Please select a fee head');
      return;
    }
    if (!newLineItem.amount) {
      toast.error('Please enter an amount');
      return;
    }
    const sub = newLineItem.sub_category || '';
    if (invoiceData.line_items.some(item => item.fee_head === newLineItem.fee_head && (item.sub_category || '') === sub)) {
      toast.error('This fee head + sub category is already added');
      return;
    }
    setInvoiceData({
      ...invoiceData,
      line_items: [...invoiceData.line_items, {
        fee_head: newLineItem.fee_head,
        sub_category: sub,
        amount: parseFloat(newLineItem.amount),
        description: newLineItem.description,
      }],
    });
    setNewLineItem({ fee_head: '', sub_category: '', amount: '', description: '' });
  };

  const removeLineItem = (index) => {
    setInvoiceData({
      ...invoiceData,
      line_items: invoiceData.line_items.filter((_, i) => i !== index),
    });
  };

  const calculateTotal = () => {
    return invoiceData.line_items.reduce((sum, item) => sum + (item.amount || 0), 0);
  };

  const handleCreateInvoice = async (forceCreate = false) => {
    if (!selectedStudent) {
      toast.error('Please select a student');
      return;
    }
    if (!invoiceData.term) {
      toast.error('Please select a term');
      return;
    }
    if (invoiceData.line_items.length === 0) {
      toast.error('Please add at least one fee item');
      return;
    }
    
    // Check for duplicates first (unless force creating)
    if (!forceCreate) {
      try {
        const feeHeads = invoiceData.line_items.map(item => item.fee_head);
        const checkResult = await api.post('/invoices/check-duplicate', {
          student_id: selectedStudent.id,
          academic_year: invoiceData.academic_year,
          term: invoiceData.term,
          fee_heads: feeHeads
        });
        
        if (checkResult.data.has_duplicate) {
          setDuplicateCheckResult(checkResult.data.existing_invoices);
          setShowDuplicateWarning(true);
          return;
        }
      } catch (err) {
        console.error('Duplicate check failed:', err);
        // Continue with creation if check fails
      }
    }
    
    setSubmitting(true);
    try {
      await api.post('/invoices/multi-line', {
        student_id: selectedStudent.id,
        academic_year: invoiceData.academic_year,
        term: invoiceData.term,
        due_date: invoiceData.due_date || null,
        line_items: invoiceData.line_items
      });
      toast.success('Invoice created successfully');
      setShowDialog(false);
      setShowDuplicateWarning(false);
      setDuplicateCheckResult(null);
      resetForm();
      fetchInvoices();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create invoice');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setSelectedStudent(null);
    setAvailableFees([]);
    setInvoiceData({
      academic_year: '2025-2026',
      term: '',
      due_date: '',
      line_items: []
    });
    setNewLineItem({ fee_head: '', sub_category: '', amount: '', description: '' });
  };

  const downloadInvoicePdf = async (invoice) => {
    try {
      toast.loading('Generating PDF...', { id: 'pdf-download' });
      const response = await api.get(`/invoices/${invoice.id}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${invoice.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Invoice downloaded', { id: 'pdf-download' });
    } catch (err) {
      toast.error('Failed to download invoice', { id: 'pdf-download' });
      console.error(err);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return <Badge className="bg-[#5556A8]/10 text-[#5556A8]">Paid</Badge>;
      case 'partial':
        return <Badge className="bg-[#D99C3B]/10 text-[#D99C3B]">Partial</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-600">Pending</Badge>;
    }
  };

  const feeHeads = [
    'tuition', 'club', 'uniform', 'lunch', 'transport', 'examination',
    'id_cards', 'registration', 'capital_levy', 'day_care', 'field_trips',
    'jersey', 'textbooks', 'locker_keys', 'others'
  ];

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1C1F1D]">Invoices</h1>
          <p className="text-[#5C635F] mt-1">{total} invoices generated</p>
        </div>
        
        {/* Create Invoice Button */}
        <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#3E3F96] hover:bg-[#2D2E7A]" data-testid="create-invoice-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Invoice
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create Invoice</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Student Selection */}
              <div className="space-y-2">
                <Label>Student *</Label>
                {selectedStudent ? (
                  <div className="flex items-center justify-between p-3 bg-[#F5F5FA] rounded-lg">
                    <div>
                      <p className="font-medium">{selectedStudent.first_name} {selectedStudent.last_name}</p>
                      <p className="text-sm text-[#5C635F]">{selectedStudent.student_id} - {selectedStudent.branch} - {selectedStudent.current_class}</p>
                    </div>
                    <Button type="button" variant="ghost" size="sm" onClick={() => { setSelectedStudent(null); setAvailableFees([]); }}>Change</Button>
                  </div>
                ) : (
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#5C635F]" />
                    <Input
                      placeholder="Search student by name or ID..."
                      value={studentSearch}
                      onChange={(e) => { setStudentSearch(e.target.value); searchStudents(e.target.value); }}
                      className="pl-9"
                      data-testid="student-search-input"
                    />
                    {searchingStudent && <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 animate-spin" />}
                    {studentResults.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-[#D8D9E8] rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {studentResults.map((student) => (
                          <button
                            key={student.id}
                            type="button"
                            className="w-full px-3 py-2 text-left hover:bg-[#F5F5FA] flex items-center justify-between"
                            onClick={() => handleStudentSelect(student)}
                          >
                            <div>
                              <span className="font-medium">{student.first_name} {student.last_name}</span>
                              <span className="text-xs text-[#5C635F] ml-2">{student.branch} - {student.current_class}</span>
                            </div>
                            <span className="text-sm text-[#5C635F]">{student.student_id}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Academic Year *</Label>
                  <Select value={invoiceData.academic_year} onValueChange={(v) => setInvoiceData({ ...invoiceData, academic_year: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(config?.academic_years || ['2024-2025', '2025-2026', '2026-2027']).map((y) => (
                        <SelectItem key={y} value={y}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Term *</Label>
                  <Select value={invoiceData.term} onValueChange={handleTermChange}>
                    <SelectTrigger data-testid="term-select"><SelectValue placeholder="Select term" /></SelectTrigger>
                    <SelectContent>
                      {config?.terms?.map((term) => (
                        <SelectItem key={term} value={term}>{term}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Due Date</Label>
                  <Input
                    type="date"
                    value={invoiceData.due_date}
                    onChange={(e) => setInvoiceData({ ...invoiceData, due_date: e.target.value })}
                  />
                </div>
              </div>

              {/* Fee Line Items */}
              <div className="space-y-3 pt-4 border-t">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">Fee Items</Label>
                  {loadingFees && <Loader2 className="w-4 h-4 animate-spin" />}
                </div>
                
                {/* Add new line item */}
                <div className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-3">
                    <Label className="text-xs">Fee Head</Label>
                    <Select value={newLineItem.fee_head} onValueChange={handleFeeHeadSelect}>
                      <SelectTrigger data-testid="fee-head-select"><SelectValue placeholder="Select fee" /></SelectTrigger>
                      <SelectContent>
                        {getFeeHeadOptions().map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-3">
                    <Label className="text-xs">Sub Category</Label>
                    {(() => {
                      const subs = getSubCategoriesForHead(newLineItem.fee_head);
                      if (subs.length > 1) {
                        return (
                          <Select value={newLineItem.sub_category} onValueChange={handleSubCategorySelect}>
                            <SelectTrigger data-testid="sub-category-select"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                              {subs.map((s) => (
                                <SelectItem key={s.sub_category || '__default__'} value={s.sub_category}>
                                  {s.sub_category || '— default —'} ({formatCurrency(s.amount, s.currency)})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        );
                      }
                      return (
                        <Input
                          value={newLineItem.sub_category}
                          onChange={(e) => setNewLineItem({ ...newLineItem, sub_category: e.target.value })}
                          placeholder="Optional"
                        />
                      );
                    })()}
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Amount (USD)</Label>
                    <Input
                      type="number"
                      value={newLineItem.amount}
                      onChange={(e) => setNewLineItem({ ...newLineItem, amount: e.target.value })}
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div className="col-span-3">
                    <Label className="text-xs">Description (Optional)</Label>
                    <Input
                      value={newLineItem.description}
                      onChange={(e) => setNewLineItem({ ...newLineItem, description: e.target.value })}
                      placeholder="e.g., Term 1 fees"
                    />
                  </div>
                  <div className="col-span-1">
                    <Button
                      type="button"
                      size="sm"
                      onClick={addLineItem}
                      className="bg-[#3E3F96] hover:bg-[#2D2E7A]"
                      data-testid="add-line-item-btn"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Line items list */}
                {invoiceData.line_items.length > 0 && (
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-[#F5F5FA]">
                          <TableHead className="text-xs">Fee Head</TableHead>
                          <TableHead className="text-xs">Sub Category</TableHead>
                          <TableHead className="text-xs">Description</TableHead>
                          <TableHead className="text-xs text-right">Amount</TableHead>
                          <TableHead className="text-xs w-10"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {invoiceData.line_items.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              {config?.fee_head_display?.[item.fee_head] || item.fee_head.replace(/_/g, ' ')}
                            </TableCell>
                            <TableCell className="text-[#5C635F]">{item.sub_category || '—'}</TableCell>
                            <TableCell className="text-[#5C635F]">{item.description || '-'}</TableCell>
                            <TableCell className="text-right tabular-nums">{formatCurrency(item.amount, 'USD')}</TableCell>
                            <TableCell>
                              <Button variant="ghost" size="sm" onClick={() => removeLineItem(index)} className="text-[#C85A40]">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="bg-[#F5F5FA]">
                          <TableCell colSpan={3} className="font-semibold">Total</TableCell>
                          <TableCell className="text-right font-bold tabular-nums text-[#3E3F96]">
                            {formatCurrency(calculateTotal(), 'USD')}
                          </TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
                <Button 
                  onClick={handleCreateInvoice}
                  className="bg-[#3E3F96] hover:bg-[#2D2E7A]"
                  disabled={submitting || !selectedStudent || !invoiceData.term || invoiceData.line_items.length === 0}
                  data-testid="submit-invoice-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Create Invoice
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        
        {/* Duplicate Warning Dialog */}
        <Dialog open={showDuplicateWarning} onOpenChange={setShowDuplicateWarning}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-[#C85A40]">Duplicate Invoice Warning</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-[#5C635F]">
                An invoice with the same student, term, and fee head combination already exists:
              </p>
              
              {duplicateCheckResult && duplicateCheckResult.length > 0 && (
                <div className="bg-[#F5F5FA] rounded-lg p-4 space-y-2 max-h-48 overflow-y-auto">
                  {duplicateCheckResult.map((inv, idx) => (
                    <div key={idx} className="text-sm border-b border-[#D8D9E8] pb-2 last:border-0">
                      <div className="flex justify-between">
                        <span className="font-medium">{inv.invoice_number}</span>
                        <Badge className={inv.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                          {inv.status}
                        </Badge>
                      </div>
                      <div className="text-[#5C635F]">
                        Amount: {formatCurrency(inv.amount)} | Fee Heads: {inv.fee_heads?.join(', ')}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              <p className="text-sm font-medium">Do you want to create a new invoice anyway?</p>
              
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => { setShowDuplicateWarning(false); setDuplicateCheckResult(null); }}>
                  Cancel
                </Button>
                <Button 
                  className="bg-[#C85A40] hover:bg-[#a84a33]" 
                  onClick={() => handleCreateInvoice(true)}
                  disabled={submitting}
                  data-testid="force-create-invoice-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Create Anyway
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card className="border-[#D8D9E8]">
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            {/* First row - Search and Branch/Class/Term */}
            <div className="flex flex-col sm:flex-row gap-4 flex-wrap">
              <div className="relative flex-1 min-w-48">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#5C635F]" />
                <Input
                  placeholder="Search by student name or ID..."
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                  className="pl-9"
                  data-testid="invoice-search"
                />
              </div>
              <MultiSelect widthClass="w-full sm:w-36" placeholder="All Branches" testId="branch-filter"
                options={(isAdmin ? config?.branches : userBranches) || []}
                selected={branchFilter} onChange={(v) => { setBranchFilter(v); setPage(1); }} />
              <MultiSelect widthClass="w-full sm:w-36" placeholder="All Classes" testId="class-filter"
                options={config?.classes || []}
                selected={classFilter} onChange={(v) => { setClassFilter(v); setPage(1); }} />
              <MultiSelect widthClass="w-full sm:w-36" placeholder="All Terms" testId="term-filter"
                options={config?.terms || []}
                selected={termFilter} onChange={(v) => { setTermFilter(v); setPage(1); }} />
            </div>
            {/* Second row - Status, Fee Head, Dates */}
            <div className="flex flex-col sm:flex-row gap-4 flex-wrap">
              <MultiSelect widthClass="w-full sm:w-36" placeholder="All Status" testId="status-filter"
                options={[{ value: 'pending', label: 'Pending' }, { value: 'partial', label: 'Partial' }, { value: 'paid', label: 'Paid' }]}
                selected={statusFilter} onChange={(v) => { setStatusFilter(v); setPage(1); }} />
              <MultiSelect widthClass="w-full sm:w-40" placeholder="All Fee Heads" testId="fee-head-filter"
                options={(feeHeads || []).map((head) => ({ value: head, label: head.replace('_', ' ') }))}
                selected={feeHeadFilter} onChange={(v) => { setFeeHeadFilter(v); setPage(1); }} />
              <div className="flex items-center gap-2">
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
                  className="w-36"
                  data-testid="invoice-start-date"
                  placeholder="From"
                />
                <span className="text-[#5C635F] text-sm">to</span>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
                  className="w-36"
                  data-testid="invoice-end-date"
                  placeholder="To"
                />
              </div>
              {(searchQuery || branchFilter.length || classFilter.length || termFilter.length || startDate || endDate) && (
                <Button variant="ghost" size="sm" className="text-[#5C635F]" onClick={() => { 
                  setSearchQuery(''); setBranchFilter([]); setClassFilter([]); setTermFilter([]); 
                  setStartDate(''); setEndDate(''); setPage(1); 
                }}>
                  Clear filters
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Floats over the page — selecting a row must not shift the table. */}
      {isAdmin && (
        <BulkSelectionBar count={sel.size} onClear={sel.clear}>
          <BulkDeleteButton count={sel.size} deleting={bulkDeleting} onClick={handleBulkDelete} label="invoices" />
        </BulkSelectionBar>
      )}

      {/* Invoices table */}
      <Card className="border-[#D8D9E8]">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-[#3E3F96]" />
            </div>
          ) : invoices.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-[#5C635F]">
              <FileText className="w-12 h-12 mb-4 opacity-50" />
              <p>No invoices found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F5F5FA] hover:bg-[#F5F5FA]">
                    {isAdmin && (
                      <TableHead className="w-8">
                        <Checkbox
                          checked={invoices.length > 0 && invoices.every((i) => sel.isSelected(i.id))}
                          onCheckedChange={() => sel.toggleAll(invoices.map((i) => i.id))}
                          aria-label="Select all"
                        />
                      </TableHead>
                    )}
                    <TableHead className="text-xs uppercase font-semibold text-[#5C635F]">Invoice #</TableHead>
                    <TableHead className="text-xs uppercase font-semibold text-[#5C635F]">Date</TableHead>
                    <TableHead className="text-xs uppercase font-semibold text-[#5C635F]">Student</TableHead>
                    <TableHead className="text-xs uppercase font-semibold text-[#5C635F]">Fee Head</TableHead>
                    <TableHead className="text-xs uppercase font-semibold text-[#5C635F]">Term</TableHead>
                    <TableHead className="text-xs uppercase font-semibold text-[#5C635F] text-right">Amount</TableHead>
                    <TableHead className="text-xs uppercase font-semibold text-[#5C635F] text-right">Paid</TableHead>
                    <TableHead className="text-xs uppercase font-semibold text-[#5C635F] text-right">Outstanding</TableHead>
                    <TableHead className="text-xs uppercase font-semibold text-[#5C635F]">Status</TableHead>
                    <TableHead className="text-xs uppercase font-semibold text-[#5C635F]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.map((invoice) => (
                    <TableRow 
                      key={invoice.id}
                      className="hover:bg-[#F5F5FA]"
                      data-testid={`invoice-row-${invoice.invoice_number}`}
                    >
                      {isAdmin && (
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Checkbox checked={sel.isSelected(invoice.id)} onCheckedChange={() => sel.toggle(invoice.id)} aria-label="Select row" />
                        </TableCell>
                      )}
                      <TableCell
                        className="font-medium text-[#3E3F96] cursor-pointer"
                        onClick={() => navigate(`/students/${invoice.student_id}`)}
                      >
                        {invoice.invoice_number}
                        {invoice.is_multi_line && (
                          <Badge variant="outline" className="ml-2 text-xs">Multi</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-[#5C635F] text-sm whitespace-nowrap">
                        {invoice.created_at ? new Date(invoice.created_at).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                      </TableCell>
                      <TableCell 
                        className="cursor-pointer"
                        onClick={() => navigate(`/students/${invoice.student_id}`)}
                      >
                        {invoice.student_name}
                      </TableCell>
                      <TableCell className="capitalize">
                        {invoice.is_multi_line ? (
                          // Name the heads rather than just counting them: with a fee-head
                          // filter applied, "2 items" gives no way to see the filter did the
                          // right thing. Long lists still collapse to "+N" to keep the row tight.
                          (() => {
                            const heads = [...new Set((invoice.line_items || [])
                              .map((li) => li.fee_head).filter(Boolean))];
                            if (!heads.length) {
                              return <span className="text-[#5C635F]">{invoice.line_items?.length || 0} items</span>;
                            }
                            const label = (h) => config?.fee_head_display?.[h] || h.replace(/_/g, ' ');
                            return (
                              <span title={heads.map(label).join(', ')}>
                                {heads.slice(0, 2).map(label).join(', ')}
                                {heads.length > 2 && (
                                  <span className="text-[#5C635F]"> +{heads.length - 2}</span>
                                )}
                              </span>
                            );
                          })()
                        ) : (
                          invoice.fee_head?.replace('_', ' ')
                        )}
                      </TableCell>
                      <TableCell>{invoice.term}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {invoice.discount_applied && invoice.discounts_applied?.length > 0 ? (
                          <div>
                            <span className="line-through text-[#999] text-xs block">{formatCurrency(invoice.original_amount, invoice.currency)}</span>
                            <span>{formatCurrency(invoice.amount, invoice.currency)}</span>
                            <span className="text-[#4CAF50] text-xs block">{invoice.discounts_applied.length} discount(s)</span>
                          </div>
                        ) : invoice.discount_applied && invoice.original_amount ? (
                          <div>
                            <span className="line-through text-[#999] text-xs block">{formatCurrency(invoice.original_amount, invoice.currency)}</span>
                            <span>{formatCurrency(invoice.amount, invoice.currency)}</span>
                          </div>
                        ) : (
                          formatCurrency(invoice.amount, invoice.currency)
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-[#5556A8]">{formatCurrency(invoice.paid_amount, invoice.currency)}</TableCell>
                      <TableCell className="text-right tabular-nums text-[#C85A40]">{formatCurrency(invoice.outstanding, invoice.currency)}</TableCell>
                      <TableCell>{getStatusBadge(invoice.status)}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {isAdmin && (
                            invoice.tally_posted ? (
                              <Button variant="ghost" size="sm" disabled title="Posted to Tally - Locked">
                                <Lock className="w-4 h-4 text-[#999]" />
                              </Button>
                            ) : (
                              <Button variant="ghost" size="sm" onClick={() => openEdit(invoice)} title="Edit" data-testid={`edit-invoice-${invoice.invoice_number}`}>
                                <Pencil className="w-4 h-4 text-[#3E3F96]" />
                              </Button>
                            )
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => downloadInvoicePdf(invoice)}
                            title="Download PDF"
                            data-testid={`download-invoice-${invoice.invoice_number}`}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-[#5C635F]">
            Showing {((page - 1) * 25) + 1} - {Math.min(page * 25, total)} of {total}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm text-[#5C635F]">Page {page} of {totalPages}</span>
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Edit Invoice Dialog */}
      <Dialog open={!!editInvoice} onOpenChange={(open) => { if (!open) setEditInvoice(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Invoice {editInvoice?.invoice_number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Amount</Label>
                <Input type="number" value={editData.amount || ''} onChange={(e) => setEditData({ ...editData, amount: parseFloat(e.target.value) })} min="0" step="0.01" data-testid="edit-invoice-amount" />
              </div>
              <div className="space-y-2">
                <Label>Currency</Label>
                <Select value={editData.currency} onValueChange={(v) => setEditData({ ...editData, currency: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="UGX">UGX</SelectItem>
                    <SelectItem value="RWF">RWF</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Term</Label>
                <Select value={editData.term} onValueChange={(v) => setEditData({ ...editData, term: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {config?.terms?.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Due Date</Label>
                <Input type="date" value={editData.due_date || ''} onChange={(e) => setEditData({ ...editData, due_date: e.target.value })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input value={editData.description || ''} onChange={(e) => setEditData({ ...editData, description: e.target.value })} />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setEditInvoice(null)}>Cancel</Button>
              <Button className="bg-[#3E3F96] hover:bg-[#2D2E7A]" onClick={handleEditSave} disabled={editSubmitting} data-testid="save-edit-invoice">
                {editSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InvoicesPage;
